/**
 * Ticker search utilities
 * Provides fuzzy search across ticker symbols and company names
 */

let tickersData = [];

/**
 * Load ticker data from JSON file
 * Call this once on app initialization
 */
export function loadTickerData() {
  try {
    tickersData = require('../data/tickers.json');
    return tickersData;
  } catch (err) {
    console.error('Failed to load ticker data:', err);
    return [];
  }
}

/**
 * Get ticker metadata by symbol
 */
export function getTickerMetadata(symbol) {
  if (!symbol) return null;
  return tickersData.find(t => t.symbol.toUpperCase() === symbol.toUpperCase());
}

/**
 * Simple fuzzy search implementation (no dependencies)
 * Searches both symbol and company name
 */
export function searchTickers(query, limit = 50) {
  if (!query || query.trim().length === 0) {
    return tickersData.slice(0, limit);
  }

  const searchTerm = query.toUpperCase().trim();
  const results = [];
  
  for (const ticker of tickersData) {
    const symbol = ticker.symbol.toUpperCase();
    const name = ticker.name.toUpperCase();
    const type = ticker.type.toUpperCase();
    
    let score = 0;
    
    // Exact symbol match (highest priority)
    if (symbol === searchTerm) {
      score = 1000;
    }
    // Symbol starts with query
    else if (symbol.startsWith(searchTerm)) {
      score = 900 - symbol.length;
    }
    // Symbol contains query
    else if (symbol.includes(searchTerm)) {
      score = 800 - symbol.indexOf(searchTerm) * 10;
    }
    // Name starts with query
    else if (name.startsWith(searchTerm)) {
      score = 700;
    }
    // Name contains query words
    else if (name.includes(searchTerm)) {
      score = 600 - name.indexOf(searchTerm);
    }
    // Fuzzy match on name (words starting with query)
    else {
      const words = name.split(/\s+/);
      const matchingWords = words.filter(word => word.startsWith(searchTerm));
      if (matchingWords.length > 0) {
        score = 500 + matchingWords.length * 50;
      }
    }
    
    // Type boost (futures and popular ETFs slightly higher)
    if (score > 0) {
      if (type === 'FUTURES') score += 5;
      if (type === 'ETF') score += 3;
      
      results.push({ ...ticker, score });
    }
  }
  
  // Sort by score (descending) and return top results
  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
}

/**
 * Get all tickers of a specific type
 */
export function getTickersByType(type) {
  return tickersData.filter(t => t.type === type);
}

/**
 * Get all unique categories
 */
export function getCategories() {
  const categories = new Set(tickersData.map(t => t.category));
  return Array.from(categories).sort();
}

/**
 * Filter tickers by category
 */
export function getTickersByCategory(category) {
  return tickersData.filter(t => t.category === category);
}


